import React from 'react';
import { useLocation } from "react-router";
import { cn } from '../../../styles/cva-v2';
import { useNavigationState } from '../../../hooks/useNavigationState';
import { NAVIGATION, UI } from '@buzunarul/shared-constants';

/**
 * 📱 BOTTOM TAB BAR COMPONENT - Mobile Navigation
 * 
 * Componenta modernă pentru navigarea pe mobile, optimizată pentru thumb reach
 * și touch interactions. Implementează best practices 2025 pentru bottom navigation.
 * 
 * Features:
 * - Touch targets 48x48px minimum
 * - Visual feedback pentru active/inactive states
 * - Smooth animations cu CSS transitions
 * - Integration cu useNavigationState hook
 * - Iconuri SVG optimizate pentru mobile
 * - Accessibility support complet
 */

export interface BottomTabItem {
  id: string;
  label: string;
  href: string;
  icon: React.ReactNode;
  testId?: string;
}

export interface BottomTabBarProps {
  /** Lista de tab-uri pentru navigare */
  tabs: BottomTabItem[];
  /** CSS classes adiționale */
  className?: string;
  /** Data test ID pentru testare */
  testId?: string;
  /** Callback pentru click pe tab */
  onTabClick?: (tab: BottomTabItem) => void;
}

// Iconuri SVG optimizate pentru mobile (24x24px)
export const TabIcons = {
  Home: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" role="img" aria-hidden="true">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
      <polyline points="9,22 9,12 15,12 15,22"/>
    </svg>
  ),
  Transactions: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" role="img" aria-hidden="true">
      <path d="M12 1v22"/>
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </svg>
  ),
  Grid: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" role="img" aria-hidden="true">
      <rect x="3" y="3" width="7" height="7"/>
      <rect x="14" y="3" width="7" height="7"/>
      <rect x="14" y="14" width="7" height="7"/>
      <rect x="3" y="14" width="7" height="7"/>
    </svg>
  ),
  Settings: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" role="img" aria-hidden="true">
      <circle cx="12" cy="12" r="3"/>
      <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1"/>
    </svg>
  ),
};

/**
 * Tab item individual cu animații și feedback vizual
 */
const TabBarItem = ({ tab, isActive, onClick }: {
  tab: BottomTabItem;
  isActive: boolean;
  onClick: () => void;
}) => {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <button
      className={cn(
        // Base styles - touch friendly
        "flex flex-col justify-center items-center",
        "px-2 py-1 min-h-[60px] min-w-[60px]",
        "relative transition-all duration-200 ease-in-out",
        
        // Touch target optimization
        "touch-manipulation",
        
        // Active/inactive states with smooth transitions
        isActive ? [
          "text-copper-600 dark:text-copper-400",
          "transform scale-105"
        ] : [
          "text-carbon-500 dark:text-carbon-400",
          "hover:text-copper-500 dark:hover:text-copper-300",
          "active:scale-95"
        ],
        
        // Focus states pentru accessibility
        "focus-visible:outline-none focus-visible:ring-2",
        "focus-visible:ring-copper-500/20 focus-visible:ring-offset-2",
        
        // Smooth color transitions
        "transition-colors transition-transform"
      )}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      data-testid={tab.testId || `tab-${tab.id}`}
      aria-label={`Navigate to ${tab.label}`}
      aria-selected={isActive}
      aria-current={isActive ? 'page' : undefined}
      role="tab"
      tabIndex={0}
    >
      {/* Icon container cu animație */}
      <div className={cn(
        "flex items-center justify-center",
        "w-6 h-6 mb-1",
        "transition-all duration-200",
        isActive && "scale-110"
      )}>
        {tab.icon}
      </div>
      
      {/* Label cu responsive sizing și smooth transitions */}
      <span className={cn(
        "text-xs font-medium leading-tight",
        "truncate max-w-[60px]",
        "transition-all duration-200",
        isActive ? "font-semibold opacity-100" : "font-normal opacity-80"
      )}>
        {tab.label}
      </span>
      
      {/* Active indicator cu animație smooth */}
      <div className={cn(
        "absolute -top-0.5 left-1/2 transform -translate-x-1/2",
        "h-1 bg-copper-600 dark:bg-copper-400",
        "rounded-full transition-all duration-300 ease-in-out",
        isActive ? "w-8 opacity-100" : "w-0 opacity-0"
      )} />
    </button>
  );
};

/**
 * Componenta principală BottomTabBar
 */
const BottomTabBar = ({
  tabs,
  className,
  testId = 'bottom-tab-bar',
  onTabClick,
}: BottomTabBarProps) => {
  const location = useLocation();
  const { actions } = useNavigationState();
  
  // Determine active tab based on current route
  const activeTab = tabs.find(tab => {
    return location.pathname === tab.href || 
           (tab.href !== '/' && location.pathname.startsWith(tab.href));
  });

  const handleTabClick = (tab: BottomTabItem) => {
    // Update navigation state
    actions.navigateToPage(tab.href, tab.label);
    
    // Trigger custom callback
    onTabClick?.(tab);
    
    // Navigate to route
    window.history.pushState({}, '', tab.href);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  return (
    <nav
      className={cn(
        // Container positioning
        "fixed bottom-0 left-0 right-0 z-50",
        
        // Background și blur effect
        "bg-white/95 dark:bg-carbon-900/95",
        "backdrop-blur-md border-t border-carbon-200 dark:border-carbon-700",
        
        // Safe area pentru iPhone X+
        "pb-safe-area-inset-bottom",
        
        // Shadow pentru depth
        "shadow-lg shadow-carbon-500/10 dark:shadow-carbon-900/20",
        
        className
      )}
      data-testid={testId}
      role="tablist"
      aria-label="Main navigation"
    >
      <div className={cn(
        "flex justify-around items-center",
        "px-2 py-2",
        "mx-auto max-w-md" // Limit width pe tablet
      )}>
        {tabs.map((tab) => (
          <TabBarItem
            key={tab.id}
            tab={tab}
            isActive={activeTab?.id === tab.id}
            onClick={() => handleTabClick(tab)}
          />
        ))}
      </div>
    </nav>
  );
};

export default BottomTabBar; 